import { Button } from '@ads/components-react';
import * as Dialog from '@radix-ui/react-dialog';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { SerieClassStudentFormValue } from '../../../types/serieForm';
import { CourseAutocomplete } from '../CourseAutocomplete';
import { TurmaAutocomplete } from '../TurmaAutocomplete';

export interface AddTurmaModalProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /** Acionado ao confirmar — só altera o estado local do formulário da Série. */
  readonly onAdd: (classStudent: SerieClassStudentFormValue) => void;
}

/**
 * Modal "Adicionar Turma" da seção Turmas do formulário de Série. Dois
 * `Autocomplete` de busca server-side: Curso (restringe a busca de Turma via
 * `courseId`) e Turma (o valor de fato persistido, como `classStudentId`).
 *
 * Não faz nenhuma chamada de API própria — ao confirmar, só adiciona o item
 * escolhido ao array local `classStudents` do formulário pai (`onAdd`). A
 * persistência ocorre apenas quando o formulário completo da Série é salvo.
 * Fiel ao frame *Adicionar Turma* de `design/series.pen`.
 */
export function AddTurmaModal({ open, onOpenChange, onAdd }: AddTurmaModalProps) {
  const { t } = useTranslation('series');

  const [courseId, setCourseId] = useState<number | null>(null);
  const [courseLabel, setCourseLabel] = useState('');
  const [classId, setClassId] = useState<number | null>(null);
  const [classLabel, setClassLabel] = useState('');

  const reset = () => {
    setCourseId(null);
    setCourseLabel('');
    setClassId(null);
    setClassLabel('');
  };

  const handleConfirm = () => {
    if (classId === null) return;
    onAdd({ classStudentId: classId, description: classLabel });
    reset();
    onOpenChange(false);
  };

  return (
    <Dialog.Root
      open={open}
      onOpenChange={next => {
        if (!next) reset();
        onOpenChange(next);
      }}
      modal={false}
    >
      <Dialog.Portal>
        {/* Backdrop manual: com `modal={false}` o Radix não renderiza o Dialog.Overlay.
            Necessário para o dropdown dos Autocompletes (portado ao body) permanecer
            clicável — no modo modal o Radix bloqueia pointer-events fora do Content. */}
        <div
          aria-hidden="true"
          className="fixed inset-none z-50 h-screen w-screen bg-base-gray-900 bg-opacity-medium backdrop-blur-soft"
        />
        <Dialog.Content
          aria-describedby={undefined}
          onInteractOutside={e => e.preventDefault()}
          className="-translate-x-1/2 -translate-y-1/2 fixed top-1/2 left-1/2 z-50 flex w-full max-w-[28rem] flex-col gap-xxs rounded-lg bg-ctx-layout-surface p-xxs shadow-level4 focus:outline-none"
        >
          <Dialog.Title className="text-ctx-content-title ts-heading-xs">
            {t('series.classStudents.addModal.title')}
          </Dialog.Title>

          <div className="flex flex-col gap-mini">
            <CourseAutocomplete
              label={t('series.classStudents.addModal.course')}
              placeholder={t('series.classStudents.addModal.coursePlaceholder')}
              value={courseId}
              selectedLabel={courseLabel}
              onChange={(id, label) => {
                setCourseId(id);
                setCourseLabel(label);
                setClassId(null);
                setClassLabel('');
              }}
            />

            <TurmaAutocomplete
              label={t('series.classStudents.addModal.class')}
              placeholder={t('series.classStudents.addModal.classPlaceholder')}
              value={classId}
              selectedLabel={classLabel}
              {...(courseId !== null ? { courseId } : {})}
              onChange={(id, label) => {
                setClassId(id);
                setClassLabel(label);
              }}
            />
          </div>

          <div className="mt-nano flex justify-end gap-nano">
            <Dialog.Close asChild>
              <Button hierarchy="secondary" size="md" type="button">
                {t('series.classStudents.addModal.cancel')}
              </Button>
            </Dialog.Close>
            <Button
              hierarchy="primary"
              size="md"
              type="button"
              disabled={classId === null}
              onClick={handleConfirm}
            >
              {t('series.classStudents.addModal.confirm')}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
